import { FormControl, MenuItem, Select, IconButton, Icon, FormHelperText } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import React from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { IConfigurationResult, ILoginListItem, IOrganisationUnitListItem } from "./Models";
import { IChangeAdditionalDataArgs, ILoginGeneratorConfig, LoginGeneratorDialog } from "./LoginGeneratorDialog";

const useStyles = makeStyles((theme) => ({
    loginGeneratorRow: {
        display: "flex"
    },
    loginGeneratorInput: {
        flex: "1"
    },
}));

interface IProps {
	value: string,
	onChange: (v: string) => void,
    onChangedReferenceNumber: (args: string) => void,
    onChangedLocation: (args: string) => void,

	applicationId: number;
    employeeId: number;
    loginGeneratorConfig: ILoginGeneratorConfig;
}

export function LoginGeneratorField(props: IProps) {

    const classes = useStyles();
    const { t } = useTranslation();

    const [creatorOpen, setCreatorOpen] = React.useState(false);
    const [options, setOptions] = React.useState<ILoginListItem[]>([]);
    const [configuration, setConfiguration] = React.useState<IConfigurationResult>(null);
    const [organisationUnits, setOrganisationUnits] = React.useState<IOrganisationUnitListItem[]>([]);

    React.useEffect(() => {

        if (!props.employeeId) {
            return;
        }

        refreshLogins();

    }, [props.applicationId, props.employeeId]);

    React.useEffect(() => {
        let active = true;

        (async () => {
            const response = await axios.get<IConfigurationResult>("/LoginsGenerator/GetConfiguration/?applicationId=" + props.applicationId);

            if (active) {
                setConfiguration(response.data);
            }
        })();

        return () => {
            active = false;
        };
    }, [props.applicationId]);

    React.useEffect(() => {
        if (!configuration?.useOrganisationUnit)
            return;

        let active = true;

        (async () => {
            const response = await axios.get<IOrganisationUnitListItem[]>("/LoginsGenerator/GetOrganizationUnits/");

            if (active) {
                setOrganisationUnits(response.data);
            }
        })();

        return () => {
            active = false;
        };
    }, [configuration?.useOrganisationUnit]);

    function refreshLogins() {

        if (props.employeeId > 0) {
            (async () => {
                const response = await axios.get<ILoginListItem[]>("/LoginsGenerator/GetLogins/?applicationId=" + props.applicationId + "&employeeId=" + props.employeeId);
                setOptions(response.data);
            })();
        }
    }

    function onChangedLogin(login: string) {
        props.onChange(login);
        const loginOption = options.find(x => x.login === login);

        if (configuration.useReferenceNumber && loginOption?.referenceNumber && props.onChangedReferenceNumber) {
            props.onChangedReferenceNumber(loginOption.referenceNumber);
        }

        if (configuration.useOrganisationUnit && loginOption?.organizationUnitId) {
            const organizationUnit = organisationUnits.find(x => x.id === loginOption.organizationUnitId);
            if (organizationUnit) {
                props.onChangedLocation(organizationUnit.number);
            }
        }
    }

    function onCreatedLogin(args: IChangeAdditionalDataArgs) {
        props.onChange(args.login);
        refreshLogins();
        if (configuration.useReferenceNumber && args.referenceNumber && props.onChangedReferenceNumber) {
            props.onChangedReferenceNumber(args.referenceNumber);
        }
        if (configuration.useOrganisationUnit && !!args.organisationUnit && typeof (args.organisationUnit) === 'object') {
            props.onChangedLocation(args.organisationUnit.number);
        }
    }

    return <>
        <div className={classes.loginGeneratorRow}>
            <FormControl
                fullWidth
                variant="outlined"
                className={classes.loginGeneratorInput}
            >
                <Select
                    value={props.value}
                    onChange={x => onChangedLogin(x.target.value as string)}
                    label="Login"
                >
                    <MenuItem value="">
                        <em>{t("control.loginsGenerator.none")}</em>
                    </MenuItem>
                    {options.map(x => {
                        return <MenuItem key={x.login} value={x.login}>
                            {x.login}
                            {x.organizationUnitNumber && <span> &nbsp; ({x.organizationUnitNumber})</span>}
                        </MenuItem>
                    })}
                </Select>
                {props.employeeId === 0 && <FormHelperText>{t("control.loginsGenerator.noSelectedEmployee")}</FormHelperText>}
            </FormControl>
            {props.employeeId > 0 && <div>
                <IconButton
                    title={t('control.loginsGenerator.createLoginBtn')}
                    onClick={() => setCreatorOpen(true)}
                >
                    <Icon>add</Icon>
                </IconButton>
            </div>}
        </div>
        {creatorOpen && <LoginGeneratorDialog
            onClose={(req?: IChangeAdditionalDataArgs) => {
                setCreatorOpen(false);
                if (req) {
                    onCreatedLogin(req);
                }
            }}
            applicationId={props.applicationId}
            employeeId={props.employeeId}
            configuration={configuration}
            organisationUnits={organisationUnits}
            loginGeneratorConfig={props.loginGeneratorConfig}
        />}
	</>
}